/**
 * Adaptive case grouping for the CaseNavigator overview-mode.
 *
 * Topology rule (operator 2026-07-06): **always** show the CLIENT — a lawyer
 * has many clients, so which client a case belongs to is critical context (the
 * renderer shows it *discreetly* so the case stays the focus). The MATTER level
 * is shown only when a client spans **more than one** matter (otherwise it's
 * redundant and omitted for clarity — the cases render directly under the
 * client). So the tree is: Client → [Matter, only if >1] → Case.
 *
 * Pure + synchronous over a single `fetchCases()` result: every
 * `CaseListItem` already carries optional `clientId`/`matterId`, so the
 * whole structure is computed client-side with no extra fetches. Group
 * *display names* are resolved lazily at render time (the caller looks
 * them up from `useClients()`/`useMatters()`), keyed off the ids here.
 *
 * `clientId`/`matterId` are optional on `CaseListItem`. Cases missing
 * one bucket under the `UNGROUPED` key and render inline (no phantom
 * "Unknown client/matter" header), so a case never disappears.
 *
 * NOTE: distinct from `group-cases.ts` (`buildDashboardCases`), which
 * enriches cases with client names for the dashboard card grid.
 */

import type { CaseListItem } from "@/lib/lexserver-client";

/** Sentinel bucket key for cases with no clientId / matterId. */
export const UNGROUPED = "__none";

export interface CaseLeaf {
  kind: "case";
  caseItem: CaseListItem;
}

export interface GroupNode {
  kind: "group";
  /** `"unassigned"` — the client-less "assignment-pending" bucket (its `id` is
   *  the `UNGROUPED` sentinel; the renderer shows a static label, not a name). */
  groupBy: "client" | "matter" | "unassigned";
  /** The clientId or matterId this group represents (`UNGROUPED` for the
   *  unassigned bucket). */
  id: string;
  /** For matter groups: the parent clientId (when known), so renderers
   *  can resolve matter display names via `useMatters(clientId)` without
   *  guessing. Undefined for client groups and for matters under
   *  client-less cases. */
  clientId?: string;
  children: NavNode[];
}

export type NavNode = GroupNode | CaseLeaf;

function leaf(caseItem: CaseListItem): CaseLeaf {
  return { kind: "case", caseItem };
}

/** Stable insertion-ordered grouping by a string key. */
function groupBy(
  cases: readonly CaseListItem[],
  keyOf: (c: CaseListItem) => string,
): Map<string, CaseListItem[]> {
  const map = new Map<string, CaseListItem[]>();
  for (const c of cases) {
    const key = keyOf(c);
    const bucket = map.get(key);
    if (bucket) bucket.push(c);
    else map.set(key, [c]);
  }
  return map;
}

/**
 * Build the case-or-matter level under a single client. Introduces matter
 * groups ONLY when the client spans >1 distinct matter (operator 2026-07-06);
 * a single-matter (or matter-less) client lists its cases directly, so the
 * matter level never adds a redundant hop. Matter-less cases always stay inline.
 */
function underClient(clientId: string | undefined, clientCases: readonly CaseListItem[]): NavNode[] {
  const byMatter = groupBy(clientCases, (c) => c.matterId ?? UNGROUPED);
  const distinctMatters = [...byMatter.keys()].filter((k) => k !== UNGROUPED);
  if (distinctMatters.length <= 1) return clientCases.map(leaf); // 0 or 1 matter → no redundant level

  const out: NavNode[] = [];
  for (const [matterId, cases] of byMatter) {
    if (matterId === UNGROUPED) out.push(...cases.map(leaf));
    else out.push({ kind: "group", groupBy: "matter", id: matterId, clientId, children: cases.map(leaf) });
  }
  return out;
}

/**
 * Top-level grouping: **always** group by client (operator 2026-07-06 — the
 * client is critical context, shown discreetly by the renderer). Under each
 * client, the matter level appears only when that client spans >1 matter (see
 * `underClient`).
 *
 * Client-less cases (clientId null/undefined — the deferred-assignment bucket,
 * D-8/D-10) are collected into a single dedicated `"unassigned"` group emitted
 * LAST, after every real client group, so assigned work stays primary and the
 * pending cases read as "needs attention" rather than silent root-level rows.
 */
export function groupCasesForOverview(cases: readonly CaseListItem[]): NavNode[] {
  // Most-recent-first (operator 2026-07-09). `groupBy` is insertion-ordered and
  // JS sort is stable, so this ONE sort cascades to every level: cases within a
  // group, matters within a client, AND the client groups themselves (each
  // appears in the order of its most-recent case), so the tree reads recency-first
  // top-to-bottom. (The unassigned bucket still emits LAST — see below.)
  //
  // Sort key = `lastActivityAt` (when the backend provides it — the last time the
  // USER touched the case: open/chat/upload/edit) with `updatedAt` as the fallback.
  // `updatedAt` alone only reflects registry mutations (create/rename/reassign),
  // so a dedicated activity timestamp is what makes "most recent" mean "most
  // recently worked on" — tracked backend-side on the case registry (relay
  // 2026-07-09). Until it lands, the fallback keeps the order stable + useful.
  const recencyKey = (c: CaseListItem) => Date.parse(c.lastActivityAt ?? c.updatedAt) || 0;
  const sorted = [...cases].sort((a, b) => recencyKey(b) - recencyKey(a));
  const byClient = groupBy(sorted, (c) => c.clientId ?? UNGROUPED);

  const result: NavNode[] = [];
  let unassigned: readonly CaseListItem[] | undefined;
  for (const [clientId, clientCases] of byClient) {
    if (clientId === UNGROUPED) unassigned = clientCases; // defer — emit last
    else result.push({ kind: "group", groupBy: "client", id: clientId, children: underClient(clientId, clientCases) });
  }
  if (unassigned && unassigned.length > 0) {
    result.push({ kind: "group", groupBy: "unassigned", id: UNGROUPED, children: unassigned.map(leaf) });
  }
  return result;
}
